/**
 * Round bookkeeping: the input log (legal actions only), the R1 submission body for the
 * server's replay, and applying a finished round to the player state.
 */
import { Op, RULES_VERSION, Status, apply, encodeLog, toBase64Url, type Action, type CompiledLevel, type Round, type RoundConfig, type RoundEvent, type RoundOutcome } from '@mp/core';
import type { BoosterKey } from '../config/content.js';
import type { RoundBody } from './dto.js';
import { uuid } from './ids.js';
import { op } from './ops.js';
import { applyRound, type RescueMethod, type RoundSummary } from './rewards.js';
import { noteRound } from './review.js';
import type { PlayerStore } from './store.js';
import type { SyncService } from './sync.js';
import { localDay, localDayKey } from './time.js';

/** RoundRequestR1 `mode` (packages/schemas round.ts); feature rounds map to it through customRound.WIRE_MODE */
export type RoundMode = 'level' | 'daily' | 'beat' | 'help' | 'tournament' | 'duel';

export interface RoundRecord {
  roundId: string;
  mode: RoundMode;
  levelId: string;
  levelNumber: number;
  startedAt: number;
  actions: Action[];
  boosters: Partial<Record<BoosterKey, number>>;
  rescues: RescueMethod[];
  /** MO-01 server nonces of completed rewarded ads this round spends */
  adNonces: { rescue: string[]; doubleTips?: string };
}

export function newRecord(levelId: string, levelNumber: number, startedAt: number, mode: RoundMode = 'level'): RoundRecord {
  return { roundId: uuid(), mode, levelId, levelNumber, startedAt, actions: [], boosters: {}, rescues: [], adNonces: { rescue: [] } };
}

/**
 * RoundRequestR1 body. `header.cfg` is the round economy the client played with, read back
 * from the round itself (the same object createRound got), so the server's check matches.
 */
export function buildSubmission(r: Round, rec: RoundRecord, o: RoundOutcome, now: number): RoundBody {
  keepReplay(r, rec, o, now);
  const nonces = rec.adNonces;
  return {
    roundId: rec.roundId,
    mode: rec.mode,
    header: {
      rulesVersion: RULES_VERSION,
      levelId: r.L.level.id,
      levelHash: r.L.hash,
      seed: r.seed,
      assist: r.assist,
      rescueThreshold: r.cfg.rescueThreshold,
      maxRescues: r.cfg.maxRescues,
      cfg: {
        coinsOrderPop: r.cfg.coinsOrderPop,
        coinsClutterPop: r.cfg.coinsClutterPop,
        coinsSweep: r.cfg.coinsSweep,
        rescueTimeMs: r.cfg.rescueTimeMs,
      },
    },
    log: toBase64Url(encodeLog(rec.actions)),
    claimed: { result: o.result, taps: o.taps, stars: o.stars, coins: o.coins },
    boostersUsed: rec.boosters,
    rescue: rec.rescues,
    playedAt: rec.startedAt,
    localDate: localDayKey(now),
    ...(nonces.rescue.length || nonces.doubleTips ? { adNonces: { ...(nonces.rescue.length ? { rescue: nonces.rescue } : {}), ...(nonces.doubleTips ? { doubleTips: nonces.doubleTips } : {}) } } : {}),
  };
}

/** SO-04.2 replay clip: what a deterministic re-render of a finished round needs (kept in memory only). */
export interface ReplaySource {
  roundId: string;
  mode: RoundMode;
  level: CompiledLevel;
  seed: number;
  assist: number;
  /** the round economy it was played with (createRound config) */
  config: RoundConfig;
  /** the input log: legal actions at active-play time */
  actions: readonly Action[];
  outcome: RoundOutcome;
  /** device time the round ended */
  endedAt: number;
}

let lastReplay: ReplaySource | null = null;

/** Every finished round passes through buildSubmission once (campaign and feature rounds alike). */
function keepReplay(r: Round, rec: RoundRecord, o: RoundOutcome, now: number): void {
  lastReplay = { roundId: rec.roundId, mode: rec.mode, level: r.L, seed: r.seed, assist: r.assist, config: { ...r.cfg }, actions: rec.actions.slice(), outcome: o, endedAt: now };
}

/** The last round that ended this session (a win, or a fail with no rescue taken), for the share composer's replay clip. */
export function lastRoundReplay(): ReplaySource | null {
  return lastReplay;
}

export interface Finished {
  summary: RoundSummary;
  body: RoundBody;
}

/**
 * Predict rewards locally and queue the round for server verification. A won round is
 * queued `held` until its win screen closes (it may still gain a double-tips nonce).
 */
export function finishRound(store: PlayerStore, sync: SyncService, r: Round, rec: RoundRecord, now: number): Finished {
  const o = r.outcome;
  if (!o) throw new Error('finishRound: round has no outcome');
  let summary!: RoundSummary;
  store.update((s) => {
    summary = applyRound(s, rec.levelId, o, localDay(now));
    noteRound(s, { won: o.result === 'win', levelMode: rec.mode === 'level' });
  });
  const body = buildSubmission(r, rec, o, now);
  sync.enqueue(op.round(body, o.result === 'win'));
  return { summary, body };
}

/** Server refuses taps closer than this (anti-cheat physical limit); queued taps are spaced. */
export const MIN_TAP_GAP_MS = 60;

/** The server's replay budget per round (core logBudget): more are rejected, so the UI stops first. */
export const BOOSTER_ROUND_CAP: Partial<Record<BoosterKey, number>> = { shuffle: 8, magnet: 8, extraSlot: 1 };

export interface ActResult {
  events: RoundEvent[];
  /** the requested action was legal and logged */
  applied: boolean;
  /** why core refused it (e.g. 'shuffle_unsolvable'), when it did */
  illegal?: string;
}

/**
 * Drives one round through core: stamps actions with active-play time (monotonic, taps at
 * least MIN_TAP_GAP_MS apart) and logs legal actions only. Core turns any action at or
 * after the rush deadline into the timeout fail; that is a legal action and is logged too,
 * so the server replay reaches the same state.
 */
export class RoundSession {
  constructor(
    readonly round: Round,
    readonly rec: RoundRecord,
    readonly clock: { now(): number },
    public lastTapT = -MIN_TAP_GAP_MS,
  ) {}

  act(op: Op, arg?: number): ActResult {
    const r = this.round;
    let t = Math.max(Math.round(this.clock.now()), r.lastT);
    if (op === Op.Tap) t = Math.max(t, this.lastTapT + MIN_TAP_GAP_MS);
    // at or past the deadline the action only fails the round: log it as the timer tick
    if (op !== Op.Tick && r.status === Status.Playing && r.deadline > 0 && t >= r.deadline) {
      const res = this.log({ op: Op.Tick, t });
      return { events: res.events, applied: false };
    }
    const action: Action = arg === undefined ? { op, t } : { op, t, arg };
    const res = this.log(action);
    if (res.illegal !== undefined) return { events: [], applied: false, illegal: res.illegal };
    if (op === Op.Tap) this.lastTapT = t;
    return { events: res.events, applied: true };
  }

  /** Apply + log when legal; illegal actions leave no trace. */
  private log(action: Action): { events: RoundEvent[]; illegal?: string } {
    const ev = apply(this.round, action);
    const last = ev[ev.length - 1];
    if (last?.type === 'illegal') return { events: [], illegal: (last as { reason?: string }).reason ?? 'illegal' };
    // a Tick that changed nothing is not worth a log entry
    if (action.op !== Op.Tick || ev.length) this.rec.actions.push(action);
    return { events: ev };
  }
}
